/**
 * Cloud Infrastructure Cost Tracker - Analysis Functions
 * On-demand calculations for debug screen display
 * These functions are only called when viewing the debug screen
 */

import { getState, getSessionDuration } from './cost-tracker';
import { PRICING, formatBytes } from './pricing';
import {
  InfrastructureSummary,
  RankedOperation,
  StorageBreakdown,
  RtdbFeatureBreakdown,
  FirestoreCollectionBreakdown,
  FunctionBreakdown,
  RtdbChangeEvent,
  RtdbCollectionChanges,
} from './cost-tracker.types';

/**
 * Get summary for all infrastructure components
 */
export function getSummary(): InfrastructureSummary {
  const state = getState();
  const t = state.totals;

  const firestoreCost =
    t.fsReads * PRICING.FS_READ +
    t.fsWrites * PRICING.FS_WRITE +
    t.fsDeletes * PRICING.FS_DELETE;

  const storageCost =
    t.storageUploadBytes * PRICING.STORAGE_UPLOAD +
    t.storageDownloadBytes * PRICING.STORAGE_DOWNLOAD;

  const rtdbCost = t.rtdbBytes * PRICING.RTDB_DOWNLOAD;

  const functionsCost =
    t.fnCalls * PRICING.FN_INVOCATION +
    Math.ceil(t.fnTotalMs / 100) * PRICING.FN_COMPUTE_100MS;

  return {
    firestore: {
      reads: t.fsReads,
      writes: t.fsWrites,
      deletes: t.fsDeletes,
      cost: firestoreCost,
    },
    storage: {
      uploads: t.storageUploads,
      uploadBytes: t.storageUploadBytes,
      downloads: t.storageDownloads,
      downloadBytes: t.storageDownloadBytes,
      cost: storageCost,
    },
    rtdb: {
      attachments: t.rtdbAttachments,
      dataEvents: t.rtdbDataEvents,
      bytes: t.rtdbBytes,
      cost: rtdbCost,
    },
    functions: {
      calls: t.fnCalls,
      totalMs: t.fnTotalMs,
      errors: t.fnErrors,
      cost: functionsCost,
    },
    totalCost: firestoreCost + storageCost + rtdbCost + functionsCost,
    sessionMs: getSessionDuration(),
  };
}

/**
 * Get top N operations across ALL infrastructure (for summary view)
 */
export function getTopOperations(limit: number = 5): RankedOperation[] {
  const state = getState();
  const operations: RankedOperation[] = [];

  // Add Firestore operations
  for (const [key, m] of Object.entries(state.firestore)) {
    const totalOps = m.reads + m.writes + m.deletes;
    if (totalOps > 0) {
      const cost =
        m.reads * PRICING.FS_READ +
        m.writes * PRICING.FS_WRITE +
        m.deletes * PRICING.FS_DELETE;
      operations.push({
        key,
        component: 'firestore',
        metric: `${m.reads} reads${m.writes > 0 ? `, ${m.writes} writes` : ''}`,
        cost,
      });
    }
  }

  // Add Storage operations
  for (const [key, m] of Object.entries(state.storage)) {
    if (m.count > 0) {
      const isUpload = key.startsWith('upload:');
      const cost = m.bytes * (isUpload ? PRICING.STORAGE_UPLOAD : PRICING.STORAGE_DOWNLOAD);
      operations.push({
        key,
        component: 'storage',
        metric: `${m.count} files (${formatBytes(m.bytes)})`,
        cost,
      });
    }
  }

  // Add RTDB operations
  for (const [key, m] of Object.entries(state.rtdb)) {
    if (m.count > 0) {
      const cost = m.bytes * PRICING.RTDB_DOWNLOAD;
      const isAttach = key.startsWith('attach:');
      operations.push({
        key,
        component: 'rtdb',
        metric: isAttach ? `${m.count} listeners` : `${m.count} events (${formatBytes(m.bytes)})`,
        cost,
      });
    }
  }

  // Add Function operations
  for (const [key, m] of Object.entries(state.functions)) {
    if (m.calls > 0) {
      const cost =
        m.calls * PRICING.FN_INVOCATION +
        Math.ceil(m.totalMs / 100) * PRICING.FN_COMPUTE_100MS;
      operations.push({
        key,
        component: 'functions',
        metric: `${m.calls} calls`,
        cost,
      });
    }
  }

  // Sort by cost descending
  operations.sort((a, b) => b.cost - a.cost);

  // Calculate percentages
  const totalCost = operations.reduce((sum, op) => sum + op.cost, 0);
  for (const op of operations) {
    op.percentage = totalCost > 0 ? (op.cost / totalCost) * 100 : 0;
  }

  return operations.slice(0, limit);
}

/**
 * Get Firestore breakdown by collection
 */
export function getFirestoreByCollection(): FirestoreCollectionBreakdown[] {
  const state = getState();
  const byCollection: Record<string, FirestoreCollectionBreakdown> = {};

  for (const [key, m] of Object.entries(state.firestore)) {
    const [collection, feature] = key.split(':');
    if (!byCollection[collection]) {
      byCollection[collection] = {
        collection,
        totalReads: 0,
        totalWrites: 0,
        totalDeletes: 0,
        features: [],
      };
    }
    byCollection[collection].totalReads += m.reads;
    byCollection[collection].totalWrites += m.writes;
    byCollection[collection].totalDeletes += m.deletes;
    byCollection[collection].features.push({
      feature,
      reads: m.reads,
      writes: m.writes,
      deletes: m.deletes,
      percentage: 0, // Will calculate below
    });
  }

  // Calculate percentages within each collection
  const result = Object.values(byCollection);
  for (const col of result) {
    const total = col.totalReads + col.totalWrites + col.totalDeletes;
    for (const f of col.features) {
      const featureTotal = f.reads + f.writes + f.deletes;
      f.percentage = total > 0 ? (featureTotal / total) * 100 : 0;
    }
    // Sort features by total operations
    col.features.sort((a, b) => (b.reads + b.writes + b.deletes) - (a.reads + a.writes + a.deletes));
  }

  // Sort collections by total operations
  result.sort(
    (a, b) =>
      (b.totalReads + b.totalWrites + b.totalDeletes) -
      (a.totalReads + a.totalWrites + a.totalDeletes)
  );

  return result;
}

/**
 * Get Storage breakdown by feature (uploads vs downloads)
 */
export function getStorageBreakdown(): StorageBreakdown {
  const state = getState();
  const uploads: Record<string, { count: number; bytes: number }> = {};
  const downloads: Record<string, { count: number; bytes: number }> = {};

  for (const [key, m] of Object.entries(state.storage)) {
    if (key.startsWith('upload:')) {
      const feature = key.replace('upload:', '');
      uploads[feature] = m;
    } else if (key.startsWith('download:')) {
      const feature = key.replace('download:', '');
      downloads[feature] = m;
    }
  }

  return { uploads, downloads };
}

/**
 * Get RTDB breakdown by feature
 */
export function getRtdbBreakdown(): Record<string, RtdbFeatureBreakdown> {
  const state = getState();
  const byFeature: Record<string, RtdbFeatureBreakdown> = {};

  for (const [key, m] of Object.entries(state.rtdb)) {
    const [type, feature] = key.split(':');
    if (!byFeature[feature]) {
      byFeature[feature] = { attachments: 0, dataEvents: 0, bytes: 0 };
    }
    if (type === 'attach') {
      byFeature[feature].attachments += m.count;
    } else if (type === 'data') {
      byFeature[feature].dataEvents += m.count;
      byFeature[feature].bytes += m.bytes;
    }
  }

  return byFeature;
}

/**
 * Get Functions breakdown by function name
 */
export function getFunctionBreakdown(): FunctionBreakdown[] {
  const state = getState();
  const byFunction: Record<string, FunctionBreakdown> = {};

  for (const [key, m] of Object.entries(state.functions)) {
    const [fnName, feature] = key.split(':');
    if (!byFunction[fnName]) {
      byFunction[fnName] = {
        fnName,
        totalCalls: 0,
        totalMs: 0,
        totalErrors: 0,
        avgMs: 0,
        features: [],
      };
    }
    byFunction[fnName].totalCalls += m.calls;
    byFunction[fnName].totalMs += m.totalMs;
    byFunction[fnName].totalErrors += m.errors;
    byFunction[fnName].features.push({
      feature,
      calls: m.calls,
      totalMs: m.totalMs,
      avgMs: m.calls > 0 ? m.totalMs / m.calls : 0,
      errors: m.errors,
    });
  }

  // Calculate averages and sort
  const result = Object.values(byFunction);
  for (const fn of result) {
    fn.avgMs = fn.totalCalls > 0 ? fn.totalMs / fn.totalCalls : 0;
    fn.features.sort((a, b) => b.calls - a.calls);
  }

  // Sort by total calls
  result.sort((a, b) => b.totalCalls - a.totalCalls);

  return result;
}

/**
 * Get Firestore breakdown by feature (which features access which collections)
 */
export function getFirestoreByFeature(): Record<string, { collection: string; reads: number; writes: number; deletes: number }[]> {
  const state = getState();
  const byFeature: Record<string, { collection: string; reads: number; writes: number; deletes: number }[]> = {};

  for (const [key, m] of Object.entries(state.firestore)) {
    const [collection, feature] = key.split(':');
    if (!byFeature[feature]) {
      byFeature[feature] = [];
    }
    byFeature[feature].push({
      collection,
      reads: m.reads,
      writes: m.writes,
      deletes: m.deletes,
    });
  }

  // Sort collections within each feature by total operations
  for (const feature of Object.keys(byFeature)) {
    byFeature[feature].sort((a, b) => (b.reads + b.writes + b.deletes) - (a.reads + a.writes + a.deletes));
  }

  return byFeature;
}

/**
 * Get RTDB change events (most recent first)
 */
export function getRtdbChangeEvents(): RtdbChangeEvent[] {
  return getState().rtdbChanges;
}

/**
 * Get RTDB changes summarized by collection
 * Shows total changes per collection from both owned and shared sources
 */
export function getRtdbChangesByCollection(): RtdbCollectionChanges[] {
  const state = getState();
  const byCollection: Record<string, RtdbCollectionChanges> = {};

  for (const event of state.rtdbChanges) {
    if (!byCollection[event.collection]) {
      byCollection[event.collection] = {
        collection: event.collection,
        ownedChanges: 0,
        sharedChanges: 0,
        totalChanges: 0,
        lastChange: event.timestamp,
      };
    }

    if (event.source === 'owned') {
      byCollection[event.collection].ownedChanges++;
    } else {
      byCollection[event.collection].sharedChanges++;
    }
    byCollection[event.collection].totalChanges++;

    // Track most recent change
    if (event.timestamp > byCollection[event.collection].lastChange) {
      byCollection[event.collection].lastChange = event.timestamp;
    }
  }

  // Sort by total changes descending
  return Object.values(byCollection).sort((a, b) => b.totalChanges - a.totalChanges);
}
